import React, { useState, useRef, useCallback } from 'react'
import Paper from '@material-ui/core/Paper'
import MenuList from '@material-ui/core/MenuList'
import MenuItem from '@material-ui/core/MenuItem'
import Popper from '@material-ui/core/Popper'
import Button, { ButtonProps } from '@material-ui/core/Button'
import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Grow from '@material-ui/core/Grow'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { makeStyles, createStyles } from '@material-ui/core/styles'

import NavigationGroupLink from './NavigationGroupLink'
import { NavigationGroupLink as NavigationGroupLinkType } from '../../types/page'

interface Props {
  launchButtonProps: ButtonProps
  links: NavigationGroupLinkType[]
}

const NavigationGroupDropdown: React.FC<Props> = ({
  links,
  launchButtonProps,
}) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef(null)

  const handleClose = useCallback(() => setOpen(false), [])

  const handleLaunchButtonClick = useCallback<
    React.MouseEventHandler<HTMLButtonElement>
  >(
    (event) => {
      launchButtonProps.onClick?.(event)
      setOpen((o) => !o)
    },
    [launchButtonProps]
  )

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={handleLaunchButtonClick}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        variant="text"
        endIcon={<ExpandMoreIcon />}
        className={classes.button}
        {...launchButtonProps}
      />
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        className={classes.popper}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper className={classes.dropdownPaper}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  className={classes.list}
                  // @ts-ignore
                  component="div"
                >
                  {links.map((link) => (
                    <MenuItem
                      key={link.contentful_id}
                      component={NavigationGroupLink}
                      link={link}
                      className={classes.link}
                    />
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    button: {
      color: theme.palette.text.primary,
      fontSize: '.94rem',
      fontWeight: 'normal',

      '&:focus': {
        outline: 0,
        border: 0,
      },

      '&:hover, &:active': {
        color: theme.palette.primary.main,
        backgroundColor: 'transparent',
      },
    },
    popper: {
      zIndex: 2,
      top: '14px',
    },
    dropdownPaper: {
      backgroundColor: 'white',
      border: `1px solid ${
        theme.colors?.gray?.[100] ?? theme.palette.text.hint
      }`,
    },
    list: {
      fontSize: '0.9em',
    },
    link: {
      '&:hover, &:active': {
        backgroundColor: 'transparent',
      },
    },
  })
)

export default NavigationGroupDropdown
